import React, { useEffect } from 'react';
import { X, LayoutGrid, Users, HelpCircle, CreditCard, ChevronRight, Star, Building } from 'lucide-react';
import { Button } from '../ui/Button';
import { useIsMobile } from '../../Hooks/useIsMobile';
import { CONFIG } from '../../Constants/Packages';

const QuickLinksModal = ({ isOpen, onClose, onBuyNow, activeBrand, onLinkClick }) => {
  const isMobile = useIsMobile();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';

      if (isMobile) {
        window.history.pushState({ modalOpen: true }, '');
        const handlePopState = (event) => {
          window.history.pushState({ modalOpen: true }, '');
        };
        window.addEventListener('popstate', handlePopState);
        return () => {
          window.removeEventListener('popstate', handlePopState);
          document.body.style.overflow = '';
          document.documentElement.style.overflow = '';
        };
      }

      return () => {
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
      };
    }
  }, [isOpen, isMobile]);

  if (!isOpen) return null;

  const links = [
    { 
      id: 'ProductFeatures', 
      label: 'Features', 
      color: '#15304A',
      icon: <LayoutGrid className="w-4 h-4 md:w-5 md:h-5" />, 
      desc: "See modules in detail e.g. CRM, Inventory, Billing, Accounts, etc."
    },
    { 
      id: 'faqSection', 
      label: 'FAQ / Common Questions', 
      color: '#0B5D1F',
      icon: <HelpCircle className="w-4 h-4 md:w-5 md:h-5" />, 
      desc: 'e.g. Invoice Format, Support on Sundays, Mobile Access, User limits & rights'
    },
    { 
      id: 'bzvzTeam', 
      label: 'About Biziverse', 
      color: '#DBA71A',
      icon: <Building className="w-4 h-4 md:w-5 md:h-5" />, 
      desc: 'Our reputation in Electronics, E-Governance & ERP'
    },
    { 
      id: 'simplePricing', 
      label: 'Pricing', 
      color: '#1F628E',
      icon: <CreditCard className="w-4 h-4 md:w-5 md:h-5" />, 
      desc: 'Explore pricing and current offers'
    },
    { 
      id: 'testimonials', 
      label: 'Customers', 
      color: '#E88421',
      icon: <Users className="w-4 h-4 md:w-5 md:h-5" />, 
      desc: `Trusted by ${CONFIG.cntCstrsStr}+ Businesses across India`
    },
  ];

  const handleScroll = (id, label) => {
    onClose();
    setTimeout(() => {
      const element = document.getElementById(id);
      if (element) {
        const offset = 80;
        const bodyRect = document.body.getBoundingClientRect().top;
        const elementRect = element.getBoundingClientRect().top;
        const elementPosition = elementRect - bodyRect;
        const offsetPosition = elementPosition - offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  return (
    <div 
      className="fixed inset-0 z-[110] md:flex md:items-center md:justify-center md:p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
    >
      <div 
        className="bg-gray-100 w-full h-full md:h-auto md:max-w-2xl md:rounded-lg shadow-2xl overflow-hidden relative animate-in zoom-in duration-300 flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-100">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                What would you like to know more about {activeBrand === 'bz' ? 'Biziverse' : 'QikERP'}?
            </h2>
          </div>
          <button 
            onClick={onClose} 
            className="hidden md:flex p-2 hover:bg-gray-200 rounded-md transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Links Grid */}
        <div className="p-4 pt-0 space-y-4 overflow-y-auto md:overflow-hidden flex-1">
          {links.map((link) => (
            <button
              key={link.id}
              onClick={() => {
                handleScroll(link.id)
                onLinkClick?.(link.label);
              }}
              style={{ '--brand-color': link.color }}
              className="w-full flex flex-col md:flex-row md:items-center p-4 rounded-lg bg-white border border-gray-200 hover:shadow-md active:scale-[0.98] active:shadow-inner shadow-sm transition-all duration-150 group text-left cursor-pointer"
            >
              {/* Mobile Layout */}
              <div className="md:hidden flex items-center gap-3 mb-2 w-full">
                <div 
                  className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center transition-all duration-300 shadow-sm group-hover:bg-[var(--brand-color)] flex-shrink-0"
                >
                  <div className="text-[var(--brand-color)] group-hover:text-white transition-colors duration-300">
                    {link.icon}
                  </div>
                </div>
                <div className="font-bold text-gray-800 group-hover:text-gray-900 transition-colors flex-1">
                  {link.label}
                </div>
                <div className="w-7 h-7 rounded-full flex items-center justify-center bg-gray-100 group-hover:bg-primary/10 group-hover:translate-x-1 transition-all duration-150 flex-shrink-0">
                  <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-primary transition-transform duration-150 group-hover:translate-x-0.5" />
                </div>
              </div>
              <div className="md:hidden text-xs text-gray-600">
                {link.desc}
              </div>

              {/* Desktop Layout */}
              <div className="hidden md:flex md:items-start md:gap-4 w-full">
                <div 
                  className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center transition-all duration-300 shadow-sm group-hover:bg-[var(--brand-color)] group-active:scale-95 flex-shrink-0"
                >
                  <div className="scale-125 text-[var(--brand-color)] group-hover:text-white transition-colors duration-300">
                    {link.icon}
                  </div>
                </div>
                <div className="flex-1 flex flex-col">
                  <div className="font-bold text-gray-800 group-hover:text-gray-900 transition-colors mb-1">
                    {link.label}
                  </div>
                  <div className="text-sm text-gray-600">
                    {link.desc}
                  </div>
                </div>
                <div className="w-7 h-7 rounded-full flex items-center justify-center bg-gray-100 group-hover:bg-primary/10 group-hover:translate-x-1 transition-all duration-150 flex-shrink-0">
                  <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-primary transition-transform duration-150 group-hover:translate-x-0.5" />
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Footer */}
        <div className="p-4 pt-0 bg-gray-100 text-center">
          <Button 
            onClick={() => {
              onClose();
              handleScroll('simplePricing');
              onLinkClick?.('Buy Now');
            }} 
            className="w-full py-3 text-sm"
          >
            Buy Now
          </Button>
        </div>
      </div>
    </div>
  );
};

export default QuickLinksModal;